import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

function BarcodeScanner({ onScan, onClose }) {
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(true);
  const [isStopping, setIsStopping] = useState(false);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const isRunningRef = useRef(false);

  const stopScanner = useCallback(async () => {
    if (isStopping || !html5QrcodeRef.current) {
      return;
    }
    
    setIsStopping(true);
    
    try {
      if (isRunningRef.current) {
        await html5QrcodeRef.current.stop();
        isRunningRef.current = false;
      }
    } catch (err) {
      console.error("Error stopping scanner:", err);
    }
    
    // Clear the scanner element to prevent blank screen
    try {
      if (html5QrcodeRef.current) {
        html5QrcodeRef.current.clear();
      }
    } catch (err) {
      // Ignore clear errors
    }
    
    html5QrcodeRef.current = null;
  }, [isStopping]);

  useEffect(() => {
    isMountedRef.current = true;
    
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 100 },
      aspectRatio: 1.0,
      formatsToSupport: [
        0, // QR_CODE
        1, // AZTEC
        2, // CODABAR
        3, // CODE_39
        4, // CODE_93
        5, // CODE_128
        6, // DATA_MATRIX
        7, // MAXICODE
        8, // ITF
        9, // EAN_13
        10, // EAN_8
        11, // PDF_417
        12, // RSS_14
        13, // RSS_EXPANDED
        14, // UPC_A
        15, // UPC_E
        16, // UPC_EAN_EXTENSION
      ]
    };

    const startScanner = async () => {
      try {
        const html5QrCode = new Html5Qrcode("barcode-reader");
        html5QrcodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          async (decodedText) => {
            if (!isMountedRef.current) return;
            
            try {
              if (isRunningRef.current) {
                await html5QrCode.stop();
                isRunningRef.current = false;
              }
            } catch (err) {
              console.error("Error stopping scanner after scan:", err);
            }
            
            onScan(decodedText);
          },
          () => {
            // Ignore scan errors, they happen continuously while scanning
          }
        );
        
        if (isMountedRef.current) {
          isRunningRef.current = true;
          setIsStarting(false);
        } else {
          // Component unmounted during start, clean up
          await html5QrCode.stop().catch(() => {});
        }
      } catch (err) {
        console.error("Error starting scanner:", err);
        if (isMountedRef.current) {
          setError('No se pudo acceder a la cámara. Asegúrese de dar permisos.');
          setIsStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      isMountedRef.current = false;
      
      const cleanup = async () => {
        if (html5QrcodeRef.current && isRunningRef.current) {
          try {
            await html5QrcodeRef.current.stop();
            isRunningRef.current = false;
          } catch (err) {
            console.error("Error stopping scanner on cleanup:", err);
          }
          try {
            html5QrcodeRef.current.clear();
          } catch (err) {
            // Ignore clear errors
          }
        }
      };
      
      cleanup();
    };
  }, [onScan]);

  const handleClose = async () => {
    if (isStopping) return;
    
    await stopScanner();
    onClose();
  };

  return (
    <div className="barcode-scanner-overlay">
      <div className="barcode-scanner-modal">
        <div className="scanner-header">
          <h3>Escanear Código de Barras</h3>
          <button 
            onClick={handleClose} 
            className="btn btn-close"
            disabled={isStopping}
          >
            ✕
          </button>
        </div>
        {error ? (
          <div className="scanner-error">
            <p>{error}</p>
            <button onClick={onClose} className="btn btn-primary">Cerrar</button>
          </div>
        ) : (
          <>
            {isStarting && (
              <div className="scanner-loading">
                <p>Iniciando cámara...</p>
              </div>
            )}
            <div id="barcode-reader" ref={scannerRef} className="scanner-view"></div>
          </>
        )}
        <p className="scanner-instructions">Apunte la cámara hacia el código de barras</p>
      </div>
    </div>
  );
}

export default BarcodeScanner;
